import { useEffect, useState } from "react";
import { SearchableChecklist, SearchableSelect } from "@/components/shared/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SiteSelectField } from "@/components/SiteSelectField";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPLOYMENT_STATUS = ["Active", "On Leave", "Terminated"];
const DL_TYPES = ["Light", "Heavy"];
const FULL_ROLES = ["Director", "Manager"];
const APP_ROLES = ["Director", "Manager", "Accounts", "Back Office", "Site Engineer", "Mechanical", "Operator"];

function roleType(designation) {
  if (!designation) return null;
  const d = designation.toLowerCase();
  if (d.includes("operator")) return "Operator";
  if (d.includes("helper")) return "Helper";
  return null;
}

function toDateInput(v) {
  if (!v) return "";
  return new Date(v).toISOString().slice(0, 10);
}

export default function EmployeeFormDialog({ employee, sites, onClose, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!employee;
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [banks, setBanks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    designation_code: employee ? employee.role_code : "",
    name: employee?.name || "",
    mobile: employee?.mobile || "",
    father_name: employee?.father_name || "",
    dob: toDateInput(employee?.dob),
    address: employee?.address || "",
    department: employee?.department || "",
    location_site: employee?.location_site || "",
    date_of_joining: toDateInput(employee?.date_of_joining),
    employment_status: employee?.employment_status || "Active",
    salary: employee?.salary ?? "",
    aadhaar: employee?.aadhaar || "",
    pan: employee?.pan || "",
    pf_applicable: employee?.pf_applicable || false,
    uan: employee?.uan || "",
    dl_number: employee?.dl_number || "",
    dl_type: employee?.dl_type || "",
    bank_name: employee?.bank_name || "",
    account_number: employee?.account_number || "",
    ifsc: employee?.ifsc || "",
    app_role: employee?.app_role || "",
    asset_ids: employee?.asset_ids || [],
  });

  useEffect(() => {
    Promise.all([
      client.get("/masters/designation"),
      client.get("/masters/department"),
      client.get("/masters/bank"),
      client.get("/assets", { params: { include_inactive: false } }),
    ])
      .then(([d, dep, b, a]) => {
        setDesignations(d.data);
        setDepartments(dep.data);
        setBanks(b.data);
        setAssets(a.data);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  }, []);

  const selectedDesignation = designations.find((d) => d.code === form.designation_code);
  const rType = roleType(selectedDesignation?.label || employee?.designation);
  const showAssetPicker = rType === "Operator" || rType === "Helper";
  const canSetAppRole = FULL_ROLES.includes(user?.role);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleAsset = (assetId) => {
    setForm((f) => {
      const has = f.asset_ids.includes(assetId);
      if (has) return { ...f, asset_ids: f.asset_ids.filter((a) => a !== assetId) };
      if (f.asset_ids.length >= 2) {
        toast.error("Maximum 2 assets can be assigned to an Operator/Helper");
        return f;
      }
      return { ...f, asset_ids: [...f.asset_ids, assetId] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDesignation && !isEdit) return toast.error("Please select a Role Code & Designation");
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      mobile: form.mobile.trim(),
      father_name: form.father_name || null,
      dob: form.dob ? new Date(form.dob).toISOString() : null,
      address: form.address || null,
      department: form.department || null,
      location_site: form.location_site || null,
      date_of_joining: form.date_of_joining ? new Date(form.date_of_joining).toISOString() : null,
      employment_status: form.employment_status,
      salary: form.salary === "" ? null : parseFloat(form.salary),
      aadhaar: form.aadhaar || null,
      pan: form.pan || null,
      pf_applicable: form.pf_applicable,
      uan: form.uan || null,
      dl_number: form.dl_number || null,
      dl_type: form.dl_type || null,
      bank_name: form.bank_name || null,
      account_number: form.account_number || null,
      ifsc: form.ifsc || null,
      asset_ids: showAssetPicker ? form.asset_ids : [],
    };
    if (canSetAppRole && form.app_role) payload.app_role = form.app_role;
    if (selectedDesignation) {
      payload.role_code = selectedDesignation.code;
      payload.designation = selectedDesignation.label;
    }
    try {
      if (isEdit) {
        const res = await client.put(`/employees/${employee.employee_id}`, payload);
        toast.success("Employee updated");
        onSaved(res.data);
      } else {
        const res = await client.post("/employees", payload);
        toast.success(`Employee created — ${res.data.employee_id}. Generated PIN: ${res.data.generated_pin}`);
        onSaved(res.data);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="employee-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Employee — ${employee.employee_id}` : "Add Employee"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Identity</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Role Code & Designation *</label>
                <SearchableSelect testId="employee-form-designation-select" value={form.designation_code} onValueChange={(v) => set("designation_code", v)} disabled={isEdit} placeholder="Select designation"
  options={designations.map((d) => ({ value: d.code, label: d.label, testId: `employee-form-designation-option-${d.code}` }))} />
                {isEdit && <p className="text-xs text-slate-400 mt-1">Role Code / Designation cannot be changed here.</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Name *</label>
                <Input data-testid="employee-form-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Mobile *</label>
                <Input data-testid="employee-form-mobile-input" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} required maxLength={10} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Father's Name</label>
                <Input data-testid="employee-form-father-name-input" value={form.father_name} onChange={(e) => set("father_name", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date of Birth</label>
                <Input type="date" data-testid="employee-form-dob-input" value={form.dob} onChange={(e) => set("dob", e.target.value)} />
              </div>
              {canSetAppRole && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">App Role Level</label>
                  <Select value={form.app_role} onValueChange={(v) => set("app_role", v)}>
                    <SelectTrigger data-testid="employee-form-app-role-select">
                      <SelectValue placeholder="Not Set" />
                    </SelectTrigger>
                    <SelectContent>
                      {APP_ROLES.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`employee-form-app-role-option-${r}`}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Address</label>
                <Textarea data-testid="employee-form-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} />
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Work Assignment</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Department</label>
                <SearchableSelect testId="employee-form-department-select" value={form.department || ""} onValueChange={(v) => set("department", v)} placeholder="Select department"
  options={departments.map((d) => ({ value: d.label, label: d.label, testId: `employee-form-department-option-${d.code}` }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Location / Site</label>
                <SiteSelectField sites={sites} value={form.location_site} onChange={(v) => set("location_site", v)} testId="employee-form-site-select" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date of Joining</label>
                <Input type="date" data-testid="employee-form-doj-input" value={form.date_of_joining} onChange={(e) => set("date_of_joining", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Employment Status</label>
                <Select value={form.employment_status} onValueChange={(v) => set("employment_status", v)}>
                  <SelectTrigger data-testid="employee-form-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_STATUS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`employee-form-status-option-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Salary</label>
                <Input type="number" data-testid="employee-form-salary-input" value={form.salary} onChange={(e) => set("salary", e.target.value)} />
              </div>
              {showAssetPicker && (
                <div className="col-span-2">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Assigned Asset(s) — max 2</label>
                  <SearchableChecklist
                    testId="employee-form-asset-list"
                    searchPlaceholder="Search assets..."
                    onToggle={toggleAsset}
                    items={assets.map((a) => ({
                      id: a.asset_id,
                      searchText: a.label,
                      checked: form.asset_ids.includes(a.asset_id),
                      checkboxTestId: `employee-form-asset-checkbox-${a.asset_id}`,
                      content: <span className="font-mono">{a.label}</span>,
                    }))}
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Statutory & Bank</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Aadhaar Number</label>
                <Input data-testid="employee-form-aadhaar-input" value={form.aadhaar} onChange={(e) => set("aadhaar", e.target.value)} maxLength={12} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">PAN Number</label>
                <Input data-testid="employee-form-pan-input" value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} maxLength={10} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">UAN Number</label>
                <Input data-testid="employee-form-uan-input" value={form.uan} onChange={(e) => set("uan", e.target.value)} maxLength={12} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Checkbox data-testid="employee-form-pf-checkbox" checked={form.pf_applicable} onCheckedChange={(v) => set("pf_applicable", !!v)} />
                <label className="text-sm font-medium text-slate-700">PF Applicable</label>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">DL Number</label>
                <Input data-testid="employee-form-dl-number-input" value={form.dl_number} onChange={(e) => set("dl_number", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">DL Type</label>
                <Select value={form.dl_type || ""} onValueChange={(v) => set("dl_type", v)}>
                  <SelectTrigger data-testid="employee-form-dl-type-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {DL_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`employee-form-dl-type-option-${t}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Bank Name</label>
                <SearchableSelect testId="employee-form-bank-select" value={form.bank_name || ""} onValueChange={(v) => set("bank_name", v)} placeholder="Select bank"
  options={banks.map((b) => ({ value: b.label, label: b.label, testId: `employee-form-bank-option-${b.code}` }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Account Number</label>
                <Input data-testid="employee-form-account-number-input" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">IFSC</label>
                <Input data-testid="employee-form-ifsc-input" value={form.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase())} maxLength={11} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="employee-form-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="employee-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Employee"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
